import { getFeaturePanelDefinition } from "../feature-panel-registry.ts";
import type { RuntimeFeatureModule } from "../runtime-feature-registry.ts";
import {
  applyNavigationEffectValue,
  resetNavigationEffectValue,
} from "../shared/page-navigation-effects.ts";

const attribute = "data-migrosone-a11y-highlight-links";
const styleVariable = "--migrosone-a11y-link-emphasis-style";
const linkVariables = [
  styleVariable,
  "--migrosone-a11y-link-text-decoration-line",
  "--migrosone-a11y-link-text-decoration-thickness",
  "--migrosone-a11y-link-text-underline-offset",
  "--migrosone-a11y-link-outline",
  "--migrosone-a11y-link-outline-offset",
  "--migrosone-a11y-link-background",
] as const;
const allowedStyles = ["underline", "outline", "background"] as const;

type LinkStyle = (typeof allowedStyles)[number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readBooleanDefault(config: unknown) {
  if (!isRecord(config)) {
    return false;
  }

  if (typeof config.enabledByDefault === "boolean") {
    return config.enabledByDefault;
  }

  return typeof config.defaultEnabled === "boolean" ? config.defaultEnabled : false;
}

function readStyle(config: unknown): LinkStyle {
  const configuredStyle =
    isRecord(config) && typeof config.style === "string"
      ? config.style.trim()
      : "underline";

  return allowedStyles.includes(configuredStyle as LinkStyle)
    ? (configuredStyle as LinkStyle)
    : "underline";
}

function resolveEnabled(config: unknown, preference: unknown) {
  return typeof preference === "boolean" ? preference : readBooleanDefault(config);
}

function resetLinkVariables(document: Document) {
  for (const variable of linkVariables) {
    resetNavigationEffectValue({ attribute, document, variable });
  }
}

function applyLinkStyle(document: Document, style: LinkStyle) {
  applyNavigationEffectValue({
    attribute,
    document,
    value: style,
    variable: styleVariable,
  });

  if (style === "outline") {
    applyNavigationEffectValue({
      attribute,
      document,
      value: "3px solid #f7c948",
      variable: "--migrosone-a11y-link-outline",
    });
    applyNavigationEffectValue({
      attribute,
      document,
      value: "3px",
      variable: "--migrosone-a11y-link-outline-offset",
    });
    return;
  }

  if (style === "background") {
    applyNavigationEffectValue({
      attribute,
      document,
      value: "rgba(247, 201, 72, 0.35)",
      variable: "--migrosone-a11y-link-background",
    });
    return;
  }

  applyNavigationEffectValue({
    attribute,
    document,
    value: "underline",
    variable: "--migrosone-a11y-link-text-decoration-line",
  });
}

const definition = getFeaturePanelDefinition("highlight-links");

if (!definition) {
  throw new Error("Missing highlight-links feature definition");
}

export const highlightLinksFeatureModule: RuntimeFeatureModule = {
  key: "highlight-links",
  definition,
  isSupported({ document }) {
    return Boolean(document.head && document.body && document.documentElement);
  },
  renderControl({ config, document, onPreferenceChange, preference }) {
    const wrapper = document.createElement("label");
    const input = document.createElement("input");
    const text = document.createElement("span");

    wrapper.className = "migrosone-a11y-widget-feature-control";
    input.type = "checkbox";
    input.checked = resolveEnabled(config, preference);
    text.textContent = "Bağlantıları vurgula";

    input.addEventListener("change", () => {
      onPreferenceChange(input.checked);
    });

    wrapper.append(input, text);
    return wrapper;
  },
  apply({ config, document, preference }) {
    resetLinkVariables(document);
    if (!resolveEnabled(config, preference)) {
      return;
    }

    applyLinkStyle(document, readStyle(config));
  },
  reset({ config, document }) {
    resetLinkVariables(document);
    if (readBooleanDefault(config)) {
      applyLinkStyle(document, readStyle(config));
    }
  },
};
